import { addDatadogAction, addDatadogError } from "./datadog";

const LS_KEY_VERSION = "sok_fe_version";
const LS_KEY_UPDATE_ATTEMPT = "sok_update_attempt_count";

interface FeConfig {
	version: string;
}

async function fetchFeConfig(): Promise<FeConfig> {
	const res = await fetch(`/fe_config.json?_t=${Date.now()}`, { cache: "no-store" });

	if (!res.ok) throw new Error(`fe_config fetch failed: ${res.status}`);

	return res.json();
}

/**
 * Checks whether a force-update modal should be shown.
 * Returns true only if a version mismatch is detected and no prior reload has been attempted.
 */
export async function checkForUpdate(): Promise<boolean> {
	if (import.meta.env.MODE === "development") return false;

	try {
		const config = await fetchFeConfig();
		const remoteVersion = config.version;
		const storedVersion = localStorage.getItem(LS_KEY_VERSION);

		// First visit: silently record version, never show modal
		if (!storedVersion) {
			localStorage.setItem(LS_KEY_VERSION, remoteVersion);

			return false;
		}

		if (storedVersion === remoteVersion) return false;

		const attemptCount = parseInt(localStorage.getItem(LS_KEY_UPDATE_ATTEMPT) ?? "0", 10);

		// Already attempted a reload once: accept current state and stop prompting
		if (attemptCount >= 1) {
			localStorage.setItem(LS_KEY_VERSION, remoteVersion);
			localStorage.removeItem(LS_KEY_UPDATE_ATTEMPT);
			addDatadogAction("force_update_post_reload", { storedVersion, remoteVersion });

			return false;
		}

		// New mismatch, no prior attempt: show the modal
		addDatadogAction("force_update_modal_shown", { storedVersion, remoteVersion });

		return true;
	} catch (err) {
		addDatadogError(err instanceof Error ? err : new Error(String(err)), {
			context: "force_update_fetch_failed",
		});

		return false;
	}
}

export function applyUpdate(): void {
	const storedVersion = localStorage.getItem(LS_KEY_VERSION);
	const attemptCount = parseInt(localStorage.getItem(LS_KEY_UPDATE_ATTEMPT) ?? "0", 10);

	localStorage.setItem(LS_KEY_UPDATE_ATTEMPT, String(attemptCount + 1));
	addDatadogAction("force_update_reload_triggered", { storedVersion });
	window.location.reload();
}
